import { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { Printer, User, Barcode } from 'lucide-react';

export default function VendedoresBarcodesPage() {
    const [vendedores, setVendedores] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchVendedores();
    }, []);

    const fetchVendedores = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/api/users/vendedores/barcodes`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                setVendedores(data);
            } else {
                setVendedores([]);
            }
        } catch (err) {
            console.error(err);
            setVendedores([]);
        } finally {
            setLoading(false);
        }
    };

    const printBarcodes = () => {
        const printWindow = window.open('', '_blank', 'width=400,height=800');
        if (!printWindow) {
            alert('Por favor habilite las ventanas emergentes para imprimir.');
            return;
        }

        const html = `
            <!DOCTYPE html>
            <html>
                <head>
                    <title>Códigos de Barras - Vendedores</title>
                    <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
                    <style>
                        @page { margin: 10mm; }
                        body { 
                            font-family: Arial, sans-serif; 
                            margin: 10px; 
                            font-size: 14px;
                        }
                        .header { 
                            text-align: center; 
                            margin-bottom: 24px;
                            border-bottom: 2px solid #333;
                            padding-bottom: 16px;
                        }
                        .header h1 { margin: 0; font-size: 20px; }
                        .header p { margin: 8px 0 0; color: #666; font-size: 13px; }
                        .card { 
                            border: 1px solid #ddd; 
                            padding: 16px; 
                            margin-bottom: 20px; 
                            page-break-inside: avoid;
                            display: flex;
                            align-items: center;
                            gap: 20px;
                        }
                        .card-info { flex: 1; }
                        .card-name { font-weight: bold; font-size: 16px; margin-bottom: 4px; }
                        .card-role { color: #666; font-size: 13px; }
                        .card-code { font-family: monospace; font-size: 14px; margin-top: 8px; }
                        .barcode-wrap { 
                            flex-shrink: 0;
                            padding: 10px;
                            border: 1px solid #eee;
                            background: #fff;
                        }
                        .barcode-wrap svg { display: block; }
                        .footer { 
                            margin-top: 24px; 
                            text-align: center; 
                            font-size: 12px; 
                            color: #999;
                        }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>ERP Minimarket - Códigos de Vendedores</h1>
                        <p>Escanee en el POS al crear una preventa para asignar el vendedor</p>
                    </div>
                    ${vendedores.map(v => `
                        <div class="card">
                            <div class="card-info">
                                <div class="card-name">${v.name || 'Sin nombre'}</div>
                                <div class="card-role">${v.role_name || 'Vendedor'}</div>
                                <div class="card-code">Código: ${v.codigo_barra}</div>
                            </div>
                            <div class="barcode-wrap">
                                <svg id="barcode-${v.id}"></svg>
                            </div>
                        </div>
                    `).join('')}
                    <div class="footer">
                        Formato: VND + ID (6 dígitos). Imprima y recorte cada tarjeta.
                    </div>
                    <script>
                        window.onload = function() {
                            ${vendedores.map(v => `
                                try {
                                    JsBarcode("#barcode-${v.id}", "${v.codigo_barra}", {
                                        format: "CODE128",
                                        width: 2,
                                        height: 50,
                                        displayValue: false
                                    });
                                } catch(e) { console.error(e); }
                            `).join('')}
                        };
                    </script>
                </body>
            </html>
        `;

        printWindow.document.write(html);
        printWindow.document.close();
    };

    return (
        <div className="page p-6 max-w-4xl mx-auto">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                        <Barcode size={32} />
                        Códigos de Barras - Vendedores
                    </h1>
                    <p className="text-gray-500 mt-1">
                        Imprima los códigos para que cada vendedor escanee el suyo al crear una preventa en el POS
                    </p>
                </div>
                <button
                    onClick={printBarcodes}
                    disabled={loading || vendedores.length === 0}
                    className="btn-primary flex items-center gap-2 px-6 py-3"
                >
                    <Printer size={20} />
                    Imprimir todos
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            ) : vendedores.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                    <div className="bg-gray-100 p-4 rounded-full inline-flex mb-4">
                        <User size={32} className="text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">No hay vendedores configurados</h3>
                    <p className="text-gray-500 mt-1">
                        Los usuarios con rol Preventa o Vendedor aparecerán aquí con su código de barras.
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="divide-y divide-gray-100">
                        {vendedores.map((v) => (
                            <div
                                key={v.id}
                                className="flex items-center justify-between p-4 hover:bg-gray-50"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                        <User size={20} className="text-indigo-600" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900">{v.name}</div>
                                        <div className="text-sm text-gray-500">{v.role_name || 'Vendedor'}</div>
                                    </div>
                                </div>
                                <div className="font-mono text-lg font-semibold text-indigo-600">
                                    {v.codigo_barra}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
